import {
	CardShell,
	type ConcernState,
	concernNoun,
	deriveUploadProgress,
	FileThumbnail,
	failedConcerns,
	useFilePreview,
} from '@atlas/ui-components'
import { fileTypeIcon, fileTypeToken, formatBytes, typeMarker } from '@atlas/ui-domain'
import { Badge, Button, Icon, ProgressBar } from '@atlas/ui-primitives'
import { X } from 'lucide-react'

import { UPLOAD_RECOVERY_LABELS, type UploadRecoveryAction } from '@/upload/upload-store'

export type { ConcernState }

export interface UploadTileProps {
	fileName: string
	fileSize: number
	contentType: string
	/** Byte progress 0–100 for the S3 PUT. Ignored once `isConfirmed`. */
	uploadProgress: number
	isConfirming?: boolean
	isConfirmed?: boolean
	/** Per-concern state, keyed by concern id. Empty until confirm responds. */
	concerns?: Record<string, ConcernState>
	/**
	 * Axis codes whose post-classification `setAttribute` write failed. Not a
	 * concern — there is no tracked `file_processing_result` for it, so no retry
	 * is offered — just a persistent badge, visible for as long as the tile is.
	 */
	attributeFailures?: string[]
	/** The local File, used for an immediate preview before any server thumbnail exists. */
	file?: File
	/** A server-side preview (presigned thumbnail), preferred over the local one once available. */
	previewUrl?: string
	/** Pre-check rejection (type/size), distinct from a concern failing after upload. */
	rejectedReason?: string
	/** Why the upload itself stopped, distinct from a concern failing after the bytes were stored. */
	failureReason?: string
	/**
	 * What the user may do about {@link failureReason}, in the order the transport
	 * offered them. The first is the primary action. Empty or absent means the
	 * failure has no recovery path and only the message is shown.
	 */
	recoveryActions?: UploadRecoveryAction[]
	/**
	 * Did {@link failureReason} end the upload? A finalization that outlasted its
	 * automatic observation window has not — it is still running on the server —
	 * so it is reported as pending rather than as an error. Defaults to true, so a
	 * failure with no outcome information is shown as one.
	 */
	failureIsTerminal?: boolean
	onRecover?: (action: UploadRecoveryAction) => void
	/** Cancels an upload that is still transferring. Absent once cancellation is no longer legal. */
	onCancel?: () => void
	onRetryConcern?: (concernId: string) => void
	onRemove?: () => void
}

/** One failed concern: what failed, why, and a retry scoped to that concern alone. */
function ConcernFailure({ id, state, onRetry }: { id: string; state: ConcernState; onRetry?: (id: string) => void }) {
	return (
		<div className="flex items-center gap-ds-2 text-ds-xs">
			<Badge variant="danger">{concernNoun(id)} failed</Badge>
			{state.errorMessage && <span className="truncate text-ds-text-muted">{state.errorMessage}</span>}
			{onRetry && (
				<Button variant="muted" onPress={() => onRetry(id)}>
					Retry
				</Button>
			)}
		</div>
	)
}

/**
 * How a stopped upload is reported and what is offered about it.
 *
 * The actions are rendered exactly as the transport supplied them, in its
 * order, under the one set of labels the recovery vocabulary defines — so the
 * same outcome cannot be worded one way here and another way elsewhere. An
 * outcome that offers nothing still shows its message: the user has to be told
 * the upload stopped even when there is nothing to press.
 */
function UploadFailure({
	reason,
	actions,
	isTerminal,
	onRecover,
}: {
	reason: string
	actions: UploadRecoveryAction[]
	isTerminal: boolean
	onRecover?: (action: UploadRecoveryAction) => void
}) {
	return (
		<div className="flex flex-col gap-ds-1">
			<p className={isTerminal ? 'text-ds-xs text-ds-danger' : 'text-ds-xs text-ds-text-muted'}>{reason}</p>
			{actions.length > 0 && onRecover && (
				<div className="flex flex-wrap items-center gap-ds-2">
					{actions.map((action, index) => (
						<Button key={action} variant={index === 0 ? 'primary' : 'muted'} onPress={() => onRecover(action)}>
							{UPLOAD_RECOVERY_LABELS[action]}
						</Button>
					))}
				</div>
			)}
		</div>
	)
}

/**
 * A single queued or in-flight upload: preview, name, size, one progress bar
 * spanning upload *and* post-upload processing, and a per-concern error row.
 *
 * Purely presentational — every piece of state arrives as a prop, so the same
 * tile serves the Upload & Classify queue and the work-order drop zones, which
 * apply different concern sets to the same file.
 *
 * The bar is deliberately one bar, not one per concern: a user watching an
 * upload wants a single answer to "is it done yet", and the per-concern detail
 * only becomes interesting when something fails — which is when the failure
 * rows appear.
 */
export function UploadTile(props: UploadTileProps) {
	const { fileName, fileSize, contentType, file, previewUrl, rejectedReason, onRemove, onRetryConcern } = props
	const { failureReason, onRecover, onCancel } = props
	const attributeFailures = props.attributeFailures ?? []
	const concerns = props.concerns ?? {}
	const recoveryActions = props.recoveryActions ?? []
	const localPreview = useFilePreview(file, contentType)
	const isRejected = Boolean(rejectedReason)

	const progress = deriveUploadProgress({
		uploadProgress: props.uploadProgress,
		isConfirming: props.isConfirming ?? false,
		isConfirmed: props.isConfirmed ?? false,
		concerns,
	})
	const failures = failedConcerns(concerns)

	return (
		<CardShell as="li" className="flex gap-ds-3 p-ds-3">
			<FileThumbnail
				name={fileName}
				thumbnailUrl={previewUrl ?? localPreview ?? undefined}
				icon={fileTypeIcon(fileTypeToken(contentType, fileName))}
				marker={typeMarker(contentType)}
			/>

			<div className="flex min-w-0 flex-1 flex-col gap-ds-1">
				<div className="flex items-baseline gap-ds-2">
					<span className="truncate text-ds-sm text-ds-text">{fileName}</span>
					<span className="shrink-0 text-ds-xs text-ds-text-muted">{formatBytes(fileSize)}</span>
				</div>

				{isRejected ? (
					// Pre-check rejection: the bytes were never sent, so there is no bar
					// and nothing to retry — the user must pick a different file.
					<p className="text-ds-xs text-ds-danger">{rejectedReason}</p>
				) : failureReason ? (
					// The upload itself stopped. The bar is gone because it no longer
					// measures anything: what matters now is what the user can do next.
					<UploadFailure
						reason={failureReason}
						actions={recoveryActions}
						isTerminal={props.failureIsTerminal ?? true}
						onRecover={onRecover}
					/>
				) : (
					<>
						<ProgressBar
							size="sm"
							value={progress.percent}
							isIndeterminate={progress.isIndeterminate}
							aria-label={`Upload progress for ${fileName}`}
						/>
						<span className="text-ds-xs text-ds-text-muted">{progress.label}</span>
					</>
				)}

				{failures.map((id) => (
					<ConcernFailure key={id} id={id} state={concerns[id]} onRetry={onRetryConcern} />
				))}
				{attributeFailures.length > 0 && (
					// No retry affordance: attributes are a synchronous, in-request
					// write, not a tracked concern — there is nothing for a "Reprocess"
					// button to target. Fixed by re-editing the value elsewhere, which
					// already writes through the same `setAttribute`.
					<Badge variant="danger" size="sm">
						{attributeFailures.length} attribute{attributeFailures.length === 1 ? '' : 's'} not saved
					</Badge>
				)}
			</div>

			{onCancel && (
				<Button variant="muted" onPress={onCancel}>
					Cancel
				</Button>
			)}
			{onRemove && (
				<Button variant="muted" onPress={onRemove} aria-label={`Remove ${fileName}`}>
					<Icon size="sm">
						<X />
					</Icon>
				</Button>
			)}
		</CardShell>
	)
}
